//Proofed
import FilterListIcon from '@mui/icons-material/FilterList'
import ManageSearchIcon from '@mui/icons-material/ManageSearch'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import SearchIcon from '@mui/icons-material/Search'
import QueryStatsIcon from '@mui/icons-material/QueryStats'
import AssessmentIcon from '@mui/icons-material/Assessment'
import type { HowItWorksStep } from '../components/HowItWorksSection'

// Hero right-panel highlights
export const enteringUKHighlights = [
    'Monitor every relevant opportunity with zero portal trawling',
    'Qualify faster with buyer and award intelligence',
    'Write stronger bids with AI with your own documents',
    'Understand why you\'re winning and losing with a Win Review',
    'Build a repeatable BD process that scales',
]

// Challenge cards  section 2
export const enteringUKChallenges = [
    {
        heading: "You're missing tenders because they're scattered across portals",
        body: "The UK public sector market spans dozens of procurement portals. Without centralised monitoring, relevant opportunities slip past every week. You only find out when it's too late to respond",
    },
    {
        heading: "Your bid quality varies and you don't always know why you lose",
        body: "Win rates plateau when the bid process relies on individual writers working under pressure. Without a structured approach and post-loss analysis, the same mistakes repeat and the same opportunities are lost",
    },
    {
        heading: "You have no clear visibility into your pipeline or the market around you",
        body: "Making BD decisions without market data means relying on instinct. Without buyer intelligence, competitor tracking and award history, you're bidding blind, leaving strategic opportunities to rivals",
    },
]

// Recommended solution stack  section 3
export const enteringUKSolutionStack = [
    {
        Icon: SearchIcon,
        title: 'CFP Core Professional',
        body: 'Daily alerts, smart search, buyer database, competitor tracking. One email, your opportunities. Every relevant tender from every portal, filtered and delivered without the manual trawl',
        tag: 'Platform',
        link: '/products/core',
        linkLabel: 'Learn about Core Pro',
    },
    {
        Icon: AutoAwesomeIcon,
        title: 'Tender Ninja',
        body: 'Faster drafting with AI using your own documents. Upload your knowledge base and produce stronger first drafts in a fraction of the time',
        tag: 'Platform Add-on',
        link: '/products/tender-ninja',
        linkLabel: 'Learn about Tender Ninja',
    },
    {
        Icon: AssessmentIcon,
        title: 'Win Review',
        body: 'An expert analysis of your current BD strategy, win rates and submission quality. Identifies what\'s working and what isn\'t, then produces a clear action plan for measurable improvement',
        tag: 'Advisory',
        link: '/products/bid-services',
        linkLabel: 'Learn about Bid Services',
    },
]

// Journey steps  section 4
export const enteringUKSteps: HowItWorksStep[] = [
    {
        num: '1',
        Icon: FilterListIcon,
        title: 'Set Up Your Monitoring',
        body: 'Configure sector filters, value thresholds and keyword alerts in Core Pro. From day one, every relevant tender is in your inbox. Nothing slips past',
    },
    {
        num: '2',
        Icon: ManageSearchIcon,
        title: 'Qualify Every Opportunity',
        body: 'Use buyer intelligence, award history and competitor data to qualify quickly. Spend your BD time on opportunities you can win, not ones you can\'t',
    },
    {
        num: '3',
        Icon: AutoAwesomeIcon,
        title: 'Write Faster, Win More',
        body: 'Deploy Tender Ninja to cut bid writing time and improve response quality. AI combined with your own knowledge writed stronger bids that highlight your unqiue strengths',
    },
    {
        num: '4',
        Icon: TrendingUpIcon,
        title: 'Review and Improve',
        body: 'Commission a Win Review to understand your win and loss patterns. Identify what\'s costing you contracts and fix it with an expert action plan',
    },
]

// Outcomes checklist  section 5
export const enteringUKOutcomes = [
    'Never miss a relevant tender. Every portal is aggregated into one daily digest.',
    'Know which opportunities are worth pursuing before you invest a day\'s work',
    'Produce stronger bid responses faster using AI in combination with your knowledge base',
    'Understand your win and loss patterns and fix the ones that are costing you',
    'Build a BD process that\'s consistent, scalable and measurable',
]

// Self-qualification checklist  section 6
export const enteringUKQualifiers = [
    'You\'re already selling to UK or Irish public sector and want to do it better',
    'You suspect you\'re missing tenders but don\'t know which ones',
    'Your bid quality is inconsistent across the team or across writers',
    'You want data-driven BD decisions, not gut feel',
    'You\'re serious about increasing public sector revenue, not just volume',
]


// FAQs  section 8
export const enteringUKFaqs = [
    {
        q: 'How is CFP Core Pro different from free portals?',
        a: "Free portals like Find a Tender and Contracts Finder each cover a slice of the market. CFP Core Pro aggregates opportunities from all major UK procurement portals, including frameworks, DPS arrangements and pre-market notices and delivers them as a single filtered daily digest. You get complete market coverage without the daily trawl across multiple sites.",
    },
    {
        q: 'What is a Win Review and how does it work?',
        a: "A Win Review is an expert advisory engagement where our team analyses your recent bid submissions, win and loss data and BD strategy. We identify patterns in what's working, what's costing you contracts and where your process can be strengthened. You receive a written report with a prioritised action plan. It typically involves a structured briefing session followed by a two-week analysis period.",
    },
    {
        q: 'Does Tender Ninja replace our bid writers?',
        a: "Tender Ninja is a tool for your existing writers, not a replacement for them. It accelerates the drafting process by generating structured first drafts based on your own document library, highlights gaps against the buyer's requirements and cites sources so writers know exactly where the content came from. Your writers still own the strategy, evidence and final response.",
    },
    {
        q: 'How quickly can we get set up?',
        a: "CFP Core Pro can be configured and live within a day of subscribing. Tender Ninja requires uploading your document library and configuring your profile, which typically takes a few hours. A Win Review engagement usually takes 2-3 weeks from kickoff to final report. Most clients see meaningful time savings within their first week of using Core Pro.",
    },
    {
        q: 'What contract coverage does CFP Core Pro provide?',
        a: "CFP Core Pro covers all major UK public procurement portals and a wide range of local authority and NHS portals. Coverage is continuously updated as new sources are added.",
    },
    {
        q: 'Can multiple team members use the platform?',
        a: "Yes. CFP Core Professional supports multiple users under a single licence, each with their own login, filter preferences and saved searches. For larger teams or those operating across multiple sectors, additional licences allow each team member to receive a fully personalised digest without shared alert noise. Our team will advise on the right licence structure for your team size.",
    },
]

// Related solution links  section 9
export const enteringUKRelated = [
    {
        headline: 'Expand into New Sectors',
        body: 'Already performing well and looking to grow into adjacent sectors? Scale your BD operation with dedicated licences, AI bid writing and strategic review',
        link: '/solutions/expand-into-new-sectors',
        tag: 'Expanding Reach',
    },
    {
        headline: 'Improve Efficiency & Win Rates',
        body: 'Want to get more from every bid? The full CFP suite (Core Pro, Tender Ninja and Analytics) gives you the tools to bid smarter and win more',
        link: '/solutions/improve-efficiency-and-win-rates',
        tag: 'Execution Support',
    },
]
